/**
 * SettingsTeam — /website-builder/settings/team
 *
 * Iter 168 — Renamed in-UX to "Permissions & Ownership". Adds:
 *   • Owner profile card (avatar / name / email / Crown badge)
 *   • Transfer Ownership flow (two tabs: existing collaborator + any email)
 *   • Prominent "Invite Contributor" CTA (collapsed form, expands on click)
 *   • Existing team list / role swap / remove
 *
 * Backend:
 *   GET    /api/builder/team               → { members: [...] } (owner first)
 *   POST   /api/builder/team/invite         {email, role, name?}
 *   PUT    /api/builder/team/{id}           {role?, status?}
 *   DELETE /api/builder/team/{id}
 *   POST   /api/builder/team/transfer-ownership { target_email | target_user_id }
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  UserPlus, Loader2, Trash2, Mail, Crown, ShieldCheck, Eye, Edit,
  ArrowRightLeft, ChevronDown, ChevronUp, X,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { GlassCard, GradientIconTile, DashboardBackdrop } from '@/components/dashboard/dashboard-ui';
import BackToSettings from './_BackToSettings';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const ROLES = [
  { id: 'admin',  label: 'Admin',  icon: ShieldCheck, desc: 'Full access incl. billing & team' },
  { id: 'editor', label: 'Editor', icon: Edit,        desc: 'Edit content, products, orders'  },
  { id: 'viewer', label: 'Viewer', icon: Eye,         desc: 'Read-only access'                },
];

function RoleBadge({ role }) {
  if (role === 'owner') return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full bg-amber-500/15 text-amber-300 border border-amber-400/25">
      <Crown size={10} /> Owner
    </span>
  );
  const map = {
    admin:  { cls: 'bg-violet-500/15 text-violet-300 border-violet-400/25',  label: 'Admin'  },
    editor: { cls: 'bg-cyan-500/15 text-cyan-300 border-cyan-400/25',         label: 'Editor' },
    viewer: { cls: 'bg-white/8 text-white/65 border-white/15',                label: 'Viewer' },
  };
  const m = map[role] || map.viewer;
  return (
    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full border ${m.cls}`}>{m.label}</span>
  );
}

function StatusPill({ status }) {
  const map = {
    active:    'bg-emerald-500/15 text-emerald-300 border-emerald-400/25',
    pending:   'bg-amber-500/15 text-amber-300 border-amber-400/25',
    suspended: 'bg-red-500/15 text-red-300 border-red-400/25',
  };
  return (
    <span className={`text-[9.5px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${map[status] || map.pending}`}>
      {status}
    </span>
  );
}

function Avatar({ url, name, email, size = 'md' }) {
  const dims = size === 'lg' ? 'w-16 h-16 text-xl' : 'w-9 h-9 text-[12px]';
  const initial = (name?.[0] || email?.[0] || '?').toUpperCase();
  if (url) {
    return (
      <img
        src={url}
        alt={name || email}
        className={`${dims} rounded-full object-cover border border-white/15 shrink-0`}
      />
    );
  }
  return (
    <div className={`${dims} rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6] flex items-center justify-center font-bold text-white shrink-0`}>
      {initial}
    </div>
  );
}

function TransferOwnershipModal({ open, onClose, owner, members, onSuccess }) {
  const [mode, setMode] = useState('member'); // 'member' | 'email'
  const [pickedId, setPickedId] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const candidates = (members || []).filter((m) => !m.is_owner);

  const submit = async () => {
    let target_email = '';
    let target_user_id = '';
    if (mode === 'member') {
      const picked = candidates.find((c) => c.id === pickedId);
      if (!picked) { toast.error('Pick a collaborator first'); return; }
      target_email = picked.email;
    } else {
      if (!email || !email.includes('@')) { toast.error('Enter a valid email'); return; }
      target_email = email.trim().toLowerCase();
    }
    if (confirmText.toUpperCase() !== 'TRANSFER') {
      toast.error('Type TRANSFER to confirm');
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/builder/team/transfer-ownership`, { target_email, target_user_id }, { headers: headers() });
      toast.success(`Ownership transferred to ${r.data.new_owner_email}`);
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Transfer failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#15152E] border border-white/10 text-white max-w-md" data-testid="transfer-ownership-modal">
        <DialogHeader>
          <DialogTitle className="text-lg flex items-center gap-2">
            <ArrowRightLeft size={16} className="text-amber-300" /> Transfer Ownership
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="p-3 rounded-xl bg-amber-500/8 border border-amber-400/25 text-[12px] text-amber-200 leading-relaxed">
            <strong className="block mb-1">This is permanent.</strong>
            You ({owner?.email}) will lose owner privileges and become an admin. The new owner can remove you at any time.
          </div>

          {/* Mode tabs */}
          <div className="flex gap-1 p-1 rounded-xl bg-white/[0.04] border border-white/5">
            <button
              type="button"
              onClick={() => setMode('member')}
              className={`flex-1 py-1.5 rounded-lg text-[11px] uppercase tracking-wider font-bold transition-colors ${mode === 'member' ? 'bg-violet-500/20 text-violet-200' : 'text-white/55 hover:text-white'}`}
              data-testid="transfer-mode-member"
            >Existing collaborator</button>
            <button
              type="button"
              onClick={() => setMode('email')}
              className={`flex-1 py-1.5 rounded-lg text-[11px] uppercase tracking-wider font-bold transition-colors ${mode === 'email' ? 'bg-violet-500/20 text-violet-200' : 'text-white/55 hover:text-white'}`}
              data-testid="transfer-mode-email"
            >By email</button>
          </div>

          {mode === 'member' ? (
            candidates.length ? (
              <div className="space-y-1.5 max-h-64 overflow-auto">
                {candidates.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setPickedId(m.id)}
                    className={`w-full flex items-center gap-3 p-2.5 rounded-xl border text-left transition-all ${pickedId === m.id ? 'border-violet-400/60 bg-violet-500/[0.08]' : 'border-white/8 bg-white/[0.02] hover:border-white/15'}`}
                    data-testid={`transfer-pick-${m.id}`}
                  >
                    <Avatar url={m.avatar_url} name={m.name} email={m.email} />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-[13px] font-semibold truncate">{m.name || m.email}</p>
                      <p className="text-[10.5px] text-white/55 truncate">{m.email}</p>
                    </div>
                    <RoleBadge role={m.role} />
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-[12px] text-white/55 italic">You don&apos;t have any collaborators yet. Switch to &quot;By email&quot; to transfer to a new user.</p>
            )
          ) : (
            <div>
              <Label className="text-[#A5A8C9] text-[11px]">New owner&apos;s email</Label>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="new-owner@studio.com"
                type="email"
                className="bg-white/5 border-white/10 text-white text-sm mt-1.5 rounded-xl"
                data-testid="transfer-email-input"
              />
              <p className="text-[10.5px] text-white/45 mt-1">They must already have an ENTREPRENATIVES account.</p>
            </div>
          )}

          <div>
            <Label className="text-[#A5A8C9] text-[11px]">Type <span className="text-amber-300 font-bold">TRANSFER</span> to confirm</Label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="TRANSFER"
              className="bg-white/5 border-white/10 text-white text-sm mt-1.5 rounded-xl"
              data-testid="transfer-confirm-input"
            />
          </div>

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider bg-white/5 text-white/65 border border-white/10 hover:bg-white/10"
              data-testid="transfer-cancel-btn"
            >Cancel</button>
            <button
              type="button"
              onClick={submit}
              disabled={busy || confirmText.toUpperCase() !== 'TRANSFER'}
              className="flex-1 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider bg-gradient-to-r from-amber-500 to-rose-500 text-white shadow-[0_8px_24px_rgba(251,113,133,0.35)] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
              data-testid="transfer-confirm-btn"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <ArrowRightLeft size={12} />}
              {busy ? 'Transferring…' : 'Transfer'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function SettingsTeam() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [email,   setEmail]   = useState('');
  const [name,    setName]    = useState('');
  const [role,    setRole]    = useState('editor');
  const [busy,    setBusy]    = useState(false);

  const owner = members.find((m) => m.is_owner) || null;
  const others = members.filter((m) => !m.is_owner);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/builder/team`, { headers: headers() });
      setMembers(r.data?.members || []);
    } catch {
      toast.error('Failed to load team');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e?.preventDefault();
    if (!email.includes('@')) {
      toast.error('Valid email required');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/builder/team/invite`, { email, name, role }, { headers: headers() });
      toast.success(`Invite sent to ${email}`);
      setEmail(''); setName(''); setRole('editor');
      setShowInvite(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invite failed');
    } finally {
      setBusy(false);
    }
  };

  const updateRole = async (memberId, newRole) => {
    try {
      await axios.put(`${API}/builder/team/${memberId}`, { role: newRole }, { headers: headers() });
      toast.success('Role updated');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const remove = async (memberId, memberEmail) => {
    if (!(await themedConfirm(`Remove ${memberEmail} from your team?`))) return;
    try {
      await axios.delete(`${API}/builder/team/${memberId}`, { headers: headers() });
      toast.success('Member removed');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Remove failed');
    }
  };

  return (
    <div className="relative" data-testid="settings-team">
      <DashboardBackdrop />
      <div className="relative z-10 space-y-6 pb-12 max-w-4xl">
        <BackToSettings />
        <div>
          <h1 className="text-3xl font-bold text-white font-syne tracking-tight">Permissions &amp; Ownership</h1>
          <p className="text-[#A5A8C9] text-sm mt-1.5">
            You own this website. Transfer ownership to another user, or invite contributors and decide what they can edit.
          </p>
        </div>

        {/* Owner card */}
        <GlassCard glow className="p-5" data-testid="owner-card">
          {loading || !owner ? (
            <div className="flex items-center gap-2 text-white/55"><Loader2 size={14} className="animate-spin" /> Loading owner…</div>
          ) : (
            <div className="flex items-center gap-4 flex-wrap">
              <Avatar url={owner.avatar_url} name={owner.name} email={owner.email} size="lg" />
              <div className="flex-1 min-w-[180px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-white font-bold font-syne text-lg" data-testid="owner-name">{owner.name || owner.email}</p>
                  <RoleBadge role="owner" />
                </div>
                <p className="text-[12px] text-[#A5A8C9] mt-0.5" data-testid="owner-email">{owner.email}</p>
                <p className="text-[10.5px] text-white/45 mt-1.5">Site owner since {owner.created_at ? new Date(owner.created_at).toLocaleDateString() : 'launch'}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowTransfer(true)}
                className="px-4 py-2.5 rounded-xl text-[11px] font-bold uppercase tracking-wider text-amber-200 bg-amber-500/[0.08] border border-amber-400/25 hover:bg-amber-500/[0.16] hover:border-amber-400/40 transition-all flex items-center gap-1.5"
                data-testid="transfer-ownership-btn"
              >
                <ArrowRightLeft size={13} /> Transfer Ownership
              </button>
            </div>
          )}
        </GlassCard>

        {/* Invite contributor CTA + collapsible form */}
        <GlassCard className="p-5" data-testid="invite-contributor-card">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <GradientIconTile icon={UserPlus} ramp="violet" size="md" />
              <div className="min-w-0">
                <p className="text-white font-bold font-syne text-[15px]">Invite a contributor</p>
                <p className="text-[11.5px] text-white/55">They&apos;ll get an email with a join link.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowInvite((v) => !v)}
              className="px-4 py-2.5 rounded-xl text-[11px] font-bold uppercase tracking-wider text-white bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] shadow-[0_10px_24px_rgba(139,92,246,0.4)] hover:shadow-[0_14px_32px_rgba(139,92,246,0.55)] transition-all flex items-center gap-1.5"
              data-testid="invite-contributor-btn"
            >
              {showInvite ? <><ChevronUp size={13} /> Close</> : <><UserPlus size={13} /> Invite Contributor</>}
            </button>
          </div>
          {showInvite && (
            <form onSubmit={invite} className="space-y-4 mt-5 border-t border-white/8 pt-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#A5A8C9] text-[11px]">Email *</Label>
                  <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@studio.com" type="email" required className="bg-white/5 border-white/10 text-white text-sm mt-1.5 rounded-xl" data-testid="team-invite-email" />
                </div>
                <div>
                  <Label className="text-[#A5A8C9] text-[11px]">Name (optional)</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jordan Doe" className="bg-white/5 border-white/10 text-white text-sm mt-1.5 rounded-xl" data-testid="team-invite-name" />
                </div>
              </div>
              <div>
                <Label className="text-[#A5A8C9] text-[11px] mb-1.5 block">Role</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  {ROLES.map((r) => {
                    const Icon = r.icon;
                    const active = role === r.id;
                    return (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setRole(r.id)}
                        className={`text-left p-3 rounded-xl border transition-all ${active ? 'border-violet-400/60 bg-violet-500/[0.08]' : 'border-white/10 bg-white/[0.02] hover:border-white/20'}`}
                        data-testid={`team-role-${r.id}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Icon size={13} className={active ? 'text-violet-300' : 'text-white/70'} />
                          <span className="text-white font-semibold text-[12.5px]">{r.label}</span>
                        </div>
                        <p className="text-[10.5px] text-white/55 leading-snug">{r.desc}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
              <button
                type="submit"
                disabled={busy}
                className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-xs font-bold uppercase tracking-wider hover:shadow-[0_12px_30px_rgba(139,92,246,0.45)] transition-all disabled:opacity-50 flex items-center gap-2"
                data-testid="team-invite-submit"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
                Send invitation
              </button>
            </form>
          )}
        </GlassCard>

        {/* Member list */}
        <section data-testid="team-members-section">
          <h2 className="text-[15px] font-bold text-white font-syne mb-3">Contributors ({others.length})</h2>
          <div className="space-y-2">
            {loading ? (
              <div className="flex items-center gap-2 text-white/55"><Loader2 size={14} className="animate-spin" /> Loading…</div>
            ) : others.length === 0 ? (
              <GlassCard className="p-6 text-center">
                <p className="text-[13px] text-white/55">No contributors yet — invite your first teammate to start collaborating.</p>
              </GlassCard>
            ) : (
              others.map((m) => (
                <GlassCard key={m.id} className="p-4" data-testid={`team-member-${m.id}`}>
                  <div className="flex items-center gap-4 flex-wrap">
                    <Avatar url={m.avatar_url} name={m.name} email={m.email} />
                    <div className="flex-1 min-w-[150px]">
                      <p className="text-white text-sm font-semibold">{m.name || m.email}</p>
                      <p className="text-[11px] text-white/55">{m.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <RoleBadge role={m.role} />
                      <StatusPill status={m.status} />
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={m.role}
                        onChange={(e) => updateRole(m.id, e.target.value)}
                        className="bg-white/5 border border-white/10 text-white text-[11px] rounded-lg px-2 py-1.5"
                        data-testid={`team-role-select-${m.id}`}
                      >
                        {ROLES.map((r) => <option key={r.id} value={r.id} className="bg-[#15152E]">{r.label}</option>)}
                      </select>
                      <button
                        onClick={() => remove(m.id, m.email)}
                        className="p-1.5 rounded-md text-white/55 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        data-testid={`team-remove-${m.id}`}
                        title="Remove"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </GlassCard>
              ))
            )}
          </div>
        </section>
      </div>

      <TransferOwnershipModal
        open={showTransfer}
        onClose={() => setShowTransfer(false)}
        owner={owner}
        members={members}
        onSuccess={load}
      />
    </div>
  );
}
